import { FileExcelOutlined, FileJpgOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { SortOrder } from "antd/es/table/interface";

import {
  AmlRiskMessage,
  BankAccountGrid,
  BID_REQUEST_STATUS_ENUM,
  BID_STATUSES,
  CompanyRole,
  DocumentDto,
  getEnumKeys,
  NetworkType,
  QUOTE_SELECTION_ENUM,
  REVERSE_ORDER_MAP,
  RISK_LEVEL_ENUM,
  S3FileType,
  USER_ROLES,
  USER_SCOPE_ENUM,
  UserScope,
  WalletRiskLevel,
} from "interfaces";
import { MAINNET_ETH_URL, MAINNET_TRX_URL } from "constants/network";
import { hasNonDefaultValues } from "components/CommonTable/utils";

import { ReactComponent as AscSortIcon } from "images/table/AscSortIcon.svg";
import { ReactComponent as DescSortIcon } from "images/table/DescSortIcon.svg";
import { ReactComponent as DefaultSortIcon } from "images/table/DefaultSortIcon.svg";
import { ReactComponent as FilterIcon } from "images/table/FilterIcon.svg";
import { ReactComponent as ActiveFilterIcon } from "images/table/ActiveFilterIcon.svg";

import styles from "./misc.module.css";
import { QUOTE_SELECTION } from "utils/envConstants";

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function downloadFile(anchorRef: HTMLAnchorElement, url: string, name: string) {
  anchorRef.href = url;
  anchorRef.setAttribute("download", name);
  anchorRef.click();
}

export const fileExistsInObject = (documents: { [key: string]: DocumentDto[] }, type: S3FileType) => {
  return documents && documents[type] && documents[type].length > 0;
};

export const download = (url: string, filename: string) => {
  const ahref = document.createElement("a");
  document.body.appendChild(ahref);
  downloadFile(ahref, url, filename);
  document.body.removeChild(ahref);
};

export const getDownloadUrl = (file: File, type: string) => {
  if (!type) {
    return null;
  }
  const blob = new Blob([file], { type: type });
  return URL.createObjectURL(blob);
};

export const getExtension = (filename: string, type?: string) => {
  if (type) {
    return type.substring(type.lastIndexOf("/") + 1);
  }
  return filename?.toLowerCase().split(".").pop();
};

export const getGlyph = (extension: string) => {
  switch (extension) {
    case "xls":
    case "xlsx":
    case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "vnd.ms-excel":
      return "file";
    case "pdf":
      return "pdf";
    default:
      return "fileEmpty";
  }
};

export const getFileSize = (filesize: number): string => {
  if (filesize < 0) return "0 Б";

  let fileSizeInByte = filesize;
  const units = ["Б", "КБ", "МБ", "ГБ", "ТБ"];
  let index = 0;

  while (fileSizeInByte >= 1000 && index < units.length - 1) {
    fileSizeInByte /= 1000;
    index++;
  }

  if (index === 0) {
    return `${Math.ceil(fileSizeInByte)} ${units[index]}`;
  } else {
    const roundedUp = Math.ceil(fileSizeInByte * 10) / 10;
    return `${parseFloat(roundedUp.toFixed(1))} ${units[index]}`;
  }
};

export function isStatusIncluded(status: string, statuses: readonly string[]): boolean {
  return statuses.includes(status);
}

export function isStatusEarlierOrEqualThan(
  status: string,
  statusesEnum: Record<string, string>,
  statusToCompare: string
): boolean {
  return Object.keys(statusesEnum).indexOf(status) <= Object.keys(statusesEnum).indexOf(statusToCompare);
}

export function isStatusEarlierThan(
  status: string,
  statusesEnum: Record<string, string>,
  statusToCompare: string
): boolean {
  return Object.keys(statusesEnum).indexOf(status) < Object.keys(statusesEnum).indexOf(statusToCompare);
}

export function isStatusLaterOrEqualThan(
  status: string,
  statusesEnum: Record<string, string>,
  statusToCompare: string
): boolean {
  return Object.keys(statusesEnum).indexOf(status) >= Object.keys(statusesEnum).indexOf(statusToCompare);
}

export const getFileIcon = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return <FilePdfOutlined />;
    case "doc":
    case "docx":
      return <FileWordOutlined />;
    case "xls":
    case "xlsx":
      return <FileExcelOutlined />;
    case "png":
    case "jpg":
    case "jpeg":
      return <FileJpgOutlined />;
    default:
      return <FileOutlined />;
  }
};

export const getSortIcon = (props: { sortOrder: SortOrder }) => {
  const icon = (() => {
    switch (props.sortOrder) {
      case REVERSE_ORDER_MAP.asc:
        return <AscSortIcon />;
      case REVERSE_ORDER_MAP.desc:
        return <DescSortIcon />;
      case null:
        return <DefaultSortIcon />;
    }
  })();

  return <Flex className={styles.sortIconBlock}>{icon}</Flex>;
};

export const getFilterIcon = (filtered: boolean) => {
  return <>{filtered ? <ActiveFilterIcon /> : <FilterIcon />}</>;
};

type ExplorerLinkType = "address" | "transaction";
export const EXPLORER_LINK_TYPE: Record<ExplorerLinkType, ExplorerLinkType> = {
  address: "address",
  transaction: "transaction",
};
/**
 * Генерирует ссылку на блокчейн-эксплорер для адреса или транзакции
 * @param network Тип сети (ETH, TRX и т.д.)
 * @param value Адрес кошелька или хэш транзакции
 * @param type Тип ссылки: address (адрес) или transaction (транзакция)
 * @returns URL для просмотра адреса или транзакции в соответствующем эксплорере
 */
export const getBlockchainExplorerLink = (network: NetworkType, value: string, type: ExplorerLinkType): string => {
  if (!network || !value) {
    return "";
  }

  const explorers = {
    ETH: {
      mainnet: MAINNET_ETH_URL,
      address: (addr: string) => `/address/${addr}`,
      transaction: (txHash: string) => `/tx/${txHash}`,
    },
    TRX: {
      mainnet: MAINNET_TRX_URL,
      address: (addr: string) => `/#/address/${addr}`,
      transaction: (txHash: string) => `/#/transaction/${txHash}`,
    },
  };

  if (!explorers[network]) {
    return "";
  }

  const explorer = explorers[network];
  const pathGenerator = type === "address" ? explorer.address : explorer.transaction;

  return `${explorer.mainnet}${pathGenerator(value)}`;
};

export const getRoleMapping = (userScopes: UserScope[]) => {
  if (
    [
      USER_SCOPE_ENUM.RISK_MANAGER,
      USER_SCOPE_ENUM.SENIOR_OPERATOR,
      USER_SCOPE_ENUM.JUNIOR_OPERATOR,
      USER_SCOPE_ENUM.USER_ADMIN,
      USER_SCOPE_ENUM.SYSTEM_ADMIN,
    ].some((scope) => userScopes.includes(scope))
  ) {
    return "OPERATOR";
  }

  if ([USER_SCOPE_ENUM.SENIOR_CUSTOMER, USER_SCOPE_ENUM.JUNIOR_CUSTOMER].some((scope) => userScopes.includes(scope))) {
    return "CUSTOMER";
  }

  if (
    [USER_SCOPE_ENUM.SENIOR_LP, USER_SCOPE_ENUM.JUNIOR_LP, USER_SCOPE_ENUM.LP_RISK_MANAGER].some((scope) =>
      userScopes.includes(scope)
    )
  ) {
    return "LIQUIDITY_PROVIDER";
  }
};

export const getAvailableUserRoles = (companyRole?: CompanyRole) => {
  const roleMapping: Record<string, Record<string, string>> = {
    CUSTOMER: {
      [USER_SCOPE_ENUM.SENIOR_CUSTOMER]: USER_ROLES.texts.SENIOR_CUSTOMER,
      [USER_SCOPE_ENUM.JUNIOR_CUSTOMER]: USER_ROLES.texts.JUNIOR_CUSTOMER,
    },
    LIQUIDITY_PROVIDER: {
      [USER_SCOPE_ENUM.SENIOR_LP]: USER_ROLES.texts.SENIOR_LP,
      [USER_SCOPE_ENUM.JUNIOR_LP]: USER_ROLES.texts.JUNIOR_LP,
      [USER_SCOPE_ENUM.LP_RISK_MANAGER]: USER_ROLES.texts.LP_RISK_MANAGER,
    },
    OPERATOR: {
      [USER_SCOPE_ENUM.SENIOR_OPERATOR]: USER_ROLES.texts.SENIOR_OPERATOR,
      [USER_SCOPE_ENUM.JUNIOR_OPERATOR]: USER_ROLES.texts.JUNIOR_OPERATOR,
      [USER_SCOPE_ENUM.RISK_MANAGER]: USER_ROLES.texts.RISK_MANAGER,
      [USER_SCOPE_ENUM.SYSTEM_ADMIN]: USER_ROLES.texts.SYSTEM_ADMIN,
      [USER_SCOPE_ENUM.USER_ADMIN]: USER_ROLES.texts.USER_ADMIN,
    },
  };

  return roleMapping[companyRole || ""] || {};
};

/**
 * Генерирует сообщение для Alert в завимсти от riskLevel(для эндпоинта request-wallet-check)
 * @returns сообщение для Alert {
    message: string;
    description?: string;
    type: "success" | "info" | "warning" | "error";
}
 */
export const getWalletRiskMessage = (riskLevel: WalletRiskLevel): AmlRiskMessage => {
  switch (riskLevel) {
    case RISK_LEVEL_ENUM.NONE:
    case RISK_LEVEL_ENUM.LOW:
      return {
        message: "Данный кошелек полностью соответствует требованиям.",
        description: "Вы можете использовать его для проведения сделок на платформе без каких-либо ограничений.",
        type: "success",
      };
    case RISK_LEVEL_ENUM.MEDIUM:
      return {
        message: "Hе рекомендуется использовать данный кошелек для проведения сделок на платформе.",
        description: "",
        type: "warning",
      };
    case RISK_LEVEL_ENUM.HIGH:
    case RISK_LEVEL_ENUM.SEVERE:
      return {
        message:
          "Выявлено критичное превышение рисковых активов / транзакций с использованием введенного адреса-идентификатора.",
        description: "Работа с указанным адресом-идентификатором на платформе невозможна. Предложите другой адрес",
        type: "error",
      };
    // case RISK_LEVEL_ENUM.TIMEOUT:
    case RISK_LEVEL_ENUM.UNDEFINED:
      return {
        message: "Hе рекомендуется использовать данный кошелек для проведения сделок на платформе.",
        description: "",
        type: "error",
      };

    default:
      return {
        message: "",
        description: "",
        type: "info",
      };
  }
};

/**
 * Генерирует сообщение для Alert в завимсти от riskLevel(для эндпоинта user-wallet-check)
 * @returns сообщение для Alert {
    message: string;
    description?: string;
    type: "success" | "info" | "warning" | "error";
}
 */
export const getAmlRiskMessage = (riskLevel: WalletRiskLevel): AmlRiskMessage => {
  switch (riskLevel) {
    case RISK_LEVEL_ENUM.NONE:
    case RISK_LEVEL_ENUM.LOW:
      return {
        message: "Данный кошелек полностью соответствует требованиям.",
        description: "Вы можете использовать его для проведения сделок на платформе без каких-либо ограничений.",
        type: "success",
      };
    case RISK_LEVEL_ENUM.MEDIUM:
      return {
        message: "Не рекомендуется использовать данный кошелек для проведения сделок на платформе.",
        description: undefined,
        type: "error",
      };
    case RISK_LEVEL_ENUM.HIGH:
    case RISK_LEVEL_ENUM.SEVERE:
      return {
        message:
          "Выявлено критичное превышение рисковых активов / транзакций с использованием введенного адреса-идентификатора.",
        description: "Работа с указанным адресом-идентификатором на платформе невозможна. Предложите другой адрес",
        type: "warning",
      };
    case RISK_LEVEL_ENUM.TIMEOUT:
      return {
        message: "Сервис проверок временно недоступен.",
        description: "Попробуйте повторить попытку позже",
        type: "warning",
      };
    default:
      return {
        message: "",
        description: "",
        type: "info",
      };
  }
};

export const getBankAlertMessage = (
  contractBik: string | undefined,
  bankAccounts: BankAccountGrid[],
  selectedAccountId: string | null
): string | undefined => {
  const selectedAccount = bankAccounts.find((account) => account.id === selectedAccountId);

  const contractBank = bankAccounts.find((account) => account.bik === contractBik)?.bankName;

  if (selectedAccount && contractBik && contractBank) {
    const isAppropriate = selectedAccount.bik === contractBik;

    if (!isAppropriate) {
      return `Рекомендуется использовать банк, в котором договор поставлен на учет ${contractBank}`;
    }
  }
};

/**
 * Универсальная функция для определения необходимости показа кнопки сброса фильтров
 * @param store Объект стора с фильтрами и сортировкой
 * @param excludeFilterKeys Массив ключей фильтров, которые следует исключить из проверки
 * @param excludeSorterKeys Массив ключей сортировок, которые следует исключить из проверки
 * @returns boolean - нужно ли показывать кнопку сброса
 */
export const shouldShowResetButton = (
  store: {
    filters?: Record<string, any>;
    defaultFilters?: Record<string, any>;
    sorters?: Record<string, any>;
    defaultSorters?: Record<string, any>;
  },
  excludeFilterKeys: string[] = []
): boolean => {
  const filters = store.filters || {};

  const nonExcludedFilters = Object.keys(filters).filter(
    (key) => !excludeFilterKeys.includes(key) && filters[key] !== undefined && filters[key] !== null
  );

  const hasNonDefaultFilters = Object.keys(filters || {}).some((key) =>
    hasNonDefaultValues(filters?.[key], store.defaultFilters?.[key])
  );

  return hasNonDefaultFilters && nonExcludedFilters.length > 0;
};

const isQuoteSelectionCustomer = QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER;
/**
 * Возвращает список фильтров для статусов заявок
 */
export const getBidFilters = () => {
  return getEnumKeys(BID_STATUSES.texts)
    .filter(
      (key) =>
        key !== BID_REQUEST_STATUS_ENUM.AML_CHECK &&
        (isQuoteSelectionCustomer ? key !== BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED : true)
    )
    .map((key) => ({
      text: BID_STATUSES.texts[key],
      value: key,
    }));
};
